import { Bot } from 'lucide-react';
import { useFloatingChat } from './FloatingChatContext';
import { useLocation } from 'react-router-dom';

/**
 * Floating chat button that appears on all pages (above the scroll-to-top button)
 */
const FloatingChatButton = () => {
  const { isOpen, isMinimized, hasUnreadMessages, setIsOpen, setIsMinimized, setHasUnreadMessages } = useFloatingChat();
  const location = useLocation();

  // Don't show button on the full chat page
  const isChatPage = location.pathname.includes('/chat');

  const handleClick = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
    // Mark messages as read when opening the chat
    setHasUnreadMessages(false);
  };

  // Don't show the button if the chat window is already open and not minimized, or if we're on the chat page
  if ((isOpen && !isMinimized) || isChatPage) {
    return null;
  }

  return (
    <button
      onClick={handleClick}
      className="
        fixed bottom-24 right-6 md:bottom-28 md:right-8
        w-14 h-14 md:w-16 md:h-16
        text-white
        rounded-full
        shadow-lg
        transition-all duration-300 ease-in-out
        z-[9998]
        flex items-center justify-center
        group
        animate-bounce-slow
      "
      style={{
        backgroundColor: 'rgb(37, 99, 235)',
        boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.5), 0 10px 10px -5px rgba(37, 99, 235, 0.4)',
        animation: 'bounce-slow 3s ease-in-out infinite',
        transition: 'transform 0.3s ease-in-out, background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      }}
      onMouseEnter={(e) => {
        const currentTransform = window.getComputedStyle(e.currentTarget).transform;
        const translateMatch = currentTransform.match(/matrix\([^,]+,\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*([^)]+)\)/);
        const currentY = translateMatch ? parseFloat(translateMatch[1]) : 0;
        
        e.currentTarget.style.backgroundColor = 'rgb(29, 78, 216)';
        e.currentTarget.style.boxShadow = '0 20px 40px -10px rgba(37, 99, 235, 0.6), 0 20px 20px -10px rgba(37, 99, 235, 0.5)';
        e.currentTarget.style.animationPlayState = 'paused';
        e.currentTarget.style.setProperty('transform', `translateY(${currentY}px) scale(1.10)`, 'important');
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'rgb(37, 99, 235)';
        e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(37, 99, 235, 0.5), 0 10px 10px -5px rgba(37, 99, 235, 0.4)';
        e.currentTarget.style.animationPlayState = 'running';
        e.currentTarget.style.removeProperty('transform');
      }}
      aria-label="Abrir chat con Finn"
    >
      <Bot className="w-6 h-6 md:w-7 md:h-7 group-hover:scale-110 transition-transform" />
      
      {/* Pulse animation for attention */}
      <span className="absolute inset-0 rounded-full bg-blue-400 opacity-0 group-hover:opacity-20 animate-ping" />
      
      {/* Badge notification dot - only show when there are unread messages */}
      {hasUnreadMessages && (
        <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse" />
      )}
      
      <style>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </button>
  );
};

export default FloatingChatButton;
